import { createElement, CSSProperties, useEffect, useState } from 'react';
import { SelectionCheckBox, SelectionRadioButton, shouldShowCheckbox } from './internal';
import { TableRegions } from '../TableRegions';
import { TableItemData } from './TableItemData';
import { useIsRTL } from '../../../locale';

import cx from 'classnames';
import cn from '../Table.css.json';
import '../Table.css';

const isRowSelected = (rowIndex: number, itemData: TableItemData): boolean | 'mixed' => {
  const { preference, getRowIdentifier, getSelectionState } = itemData;
  if (itemData.dragging === true) return false;

  const selectionState = itemData.getSelectionCheckboxState;

  const selectionStateOverride = selectionState
    ? selectionState(getRowIdentifier(rowIndex))
    : getSelectionState?.(getRowIdentifier(rowIndex));

  return selectionStateOverride !== undefined
    ? selectionStateOverride
    : preference?.selectAll === true || !!preference?.selectedRows?.has(getRowIdentifier(rowIndex));
};

const SelectionContent = (props: {
  itemData: TableItemData;
  rowIndex: number;
  style?: CSSProperties;
  className?: string;
  selectedCell?: boolean | 'mixed';
  selectAll?: boolean;
  ariaLabel?: string;
  ariaLabelledBy?: string;
}) => {
  const isRTL = useIsRTL();
  const { rowIndex, itemData } = props;
  const selected = itemData.preference?.selectAll;
  const hideUnselectedSelectionControl = itemData.hideUnselectedSelectionControl;
  const [displayState, setDisplayState] = useState<'block' | 'none'>(
    hideUnselectedSelectionControl ? 'none' : 'block'
  );
  const focused =
    itemData.lastFocusedCell?.rowIndex === rowIndex &&
    itemData.lastFocusedCell?.colIndex === 0 &&
    itemData.focusIndicator === 'visible';

  useEffect(() => {
    hideUnselectedSelectionControl && setDisplayState(selected || focused ? 'block' : 'none');
  }, [selected, focused, hideUnselectedSelectionControl]);

  useEffect(() => {
    if (itemData.hideUnselectedSelectionControl === false) setDisplayState('block');
  }, [itemData.hideUnselectedSelectionControl]);

  const { getRowIdentifier, hideSelectionControl } = itemData;
  const rowKey = getRowIdentifier(rowIndex);
  const count = itemData.preference?.selectedRows?.size || 0;
  const { selectedCell } = props;
  const getSelectionCheckboxStyle = () => {
    // when the selection column is hidden, the hiding/unhiding is handled at the cell level
    const hoverOnParent = itemData.hideSelectionControlColumn;
    if (hoverOnParent === true)
      return {
        ...props.style,
        paddingRight: isRTL ? '0px' : '5px',
        paddingLeft: isRTL ? '5px' : '0px',
      };
    return {
      ...props.style,
      ...{ display: selectedCell || count > 0 ? 'block' : displayState },
    };
  };

  return (
    <div
      className={props.className}
      onContextMenu={(event) => {
        event.preventDefault();
      }}
      onMouseDown={(e) => {
        if (e.shiftKey) e.preventDefault();
        itemData.refTop.current?.focus();
      }}
      onMouseOver={() => {
        hideUnselectedSelectionControl && setDisplayState('block');
      }}
      onMouseLeave={() => {
        hideUnselectedSelectionControl &&
          setDisplayState(selectedCell || focused ? 'block' : 'none');
      }}
    >
      {hideSelectionControl?.(rowKey) === true ? null : shouldShowCheckbox(
          itemData.selectionMode
        ) ? (
        <SelectionCheckBox
          itemData={itemData}
          rowIndex={rowIndex}
          style={getSelectionCheckboxStyle()}
          selected={selectedCell}
          selectAll={props.selectAll}
          ariaLabel={props.ariaLabel}
          ariaLabelledBy={props.ariaLabelledBy}
        />
      ) : (
        <SelectionRadioButton
          itemData={itemData}
          rowIndex={rowIndex}
          style={{ ...props.style }}
          selected={!!selectedCell}
        />
      )}
    </div>
  );
};

export const SelectAllCellContent = (props: {
  itemData: TableItemData;
  rowIndex: number;
  style?: CSSProperties;
}) => {
  const { style, itemData } = props;
  return (
    <SelectionContent
      itemData={props.itemData}
      rowIndex={-1}
      style={style}
      selectedCell={itemData.preference?.selectAll}
      className={cn.selectionCell}
      selectAll
    />
  );
};

export const SelectionCellContent = (props: {
  itemData: TableItemData;
  rowIndex: number;
  style?: CSSProperties;
  ariaLabel?: string;
  ariaLabelledBy?: string;
}) => {
  const { rowIndex, itemData } = props;
  const selectedCell = isRowSelected(rowIndex, itemData);
  const hideSelectionControlColumn = itemData.hideSelectionControlColumn;
  return (
    <SelectionContent
      itemData={props.itemData}
      style={props.style}
      rowIndex={props.rowIndex}
      selectedCell={selectedCell}
      ariaLabel={props.ariaLabel}
      ariaLabelledBy={props.ariaLabelledBy}
      className={cx(
        itemData.buildCellBorderStyles(itemData.getRowIdentifier(props.rowIndex)),
        hideSelectionControlColumn !== true && cn.selectionCell,
        selectedCell === true && cn.selected,
        (itemData.pressedCell?.region === TableRegions.Data ||
          itemData.pressedCell?.region === TableRegions.RowHeader) &&
          rowIndex === itemData.pressedCell.rowIndex &&
          itemData.pressEntireRow &&
          cn.pressed
      )}
    />
  );
};
